import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DataTransferService } from '../services/data-transfer.service';
import { ProjectService } from '../services/project.service';
import { map, mergeMap, Observable, tap } from 'rxjs';
import { DataService } from '../services/data.service';
import { ToastService } from '@siemens/ix-angular';
import { Project } from '../models/project.model';
import { FORM_FIELDS, createFormControls } from 'src/const-data/form-field-mappings';

@Component({
  selector: 'app-project-calculation',
  templateUrl: './project-calculation.component.html',
  styleUrls: ['./project-calculation.component.css'],
  standalone: false
})
export class ProjectCalculationComponent implements OnInit {
  readonly State = State;
  protected readonly stateMaschine = new WorkflowStateMachine();
  protected isSimpleCalculation = true;
  protected projectId = '';

  protected currentProject: Project | undefined;

  protected readonly basisInfoForm: FormGroup = this.formBuilder.group(createFormControls(FORM_FIELDS.BASIS_INFO));

  protected readonly equipmentInventoryForm: FormGroup = this.formBuilder.group(
    createFormControls(FORM_FIELDS.EQUIPMENT_INVENTORY)
  );

  protected readonly servicesForm: FormGroup = this.formBuilder.group(createFormControls(FORM_FIELDS.SERVICES));

  protected readonly pricingForm: FormGroup = this.formBuilder.group(createFormControls(FORM_FIELDS.PRICING));

  protected readonly contractForm: FormGroup = this.formBuilder.group(createFormControls(FORM_FIELDS.CONTRACT));

  constructor(
    protected readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly formBuilder: FormBuilder,
    private readonly dataTransferService: DataTransferService,
    private readonly projectService: ProjectService,
    private readonly dataService: DataService,
    private readonly toastService: ToastService
  ) {}

  ngOnInit() {
    this.activatedRoute.params
      .pipe(
        tap(params => {
          this.projectId = params['id']?.toString() || '';
        }),
        mergeMap(params => this.projectService.getProject(+params['id'])),
        mergeMap(project => this.getIsSimpleCalc(project.id.toString()).pipe(map(() => project)))
      )
      .subscribe({
        next: project => {
          this.currentProject = project;
        },
        error: () => {
          this.toastService.show({
            title: 'Load data error',
            message: 'We cannot load the data of the project. ',
            type: 'error'
          });
          this.currentProject = undefined;
          this.router.navigate(['../../'], { relativeTo: this.activatedRoute });
        }
      });
  }

  getIsSimpleCalc(projectId: string): Observable<void> {
    return this.dataTransferService.getValuesFromExcel([FORM_FIELDS.BASIS_INFO.SIMPLE_CALC], projectId).pipe(
      tap((formData: Record<string, unknown> | null | undefined) => {
        const simpleCalcValue = formData?.[FORM_FIELDS.BASIS_INFO.SIMPLE_CALC];
        if (simpleCalcValue === null || simpleCalcValue === undefined) {
          return;
        }

        this.isSimpleCalculation = String(simpleCalcValue) !== 'No';
      }),
      map(() => void 0)
    );
  }

  changeSimpleCalculation(isSimpleCalculation: boolean) {
    this.isSimpleCalculation = isSimpleCalculation;
  }

  selectWorkflowStep(state: State) {
    if (!this.projectId) return;
    const formCurrentState: FormGroup = this.getFormGroup(this.stateMaschine.currentState);
    this.dataService.tryWriteData(formCurrentState, this.projectId).subscribe(isWritingSuccessful => {
      if (isWritingSuccessful) {
        this.stateMaschine.setState(state);
      }
    });
  }

  getFormGroup(currentState: State): FormGroup {
    switch (currentState) {
      case State.Step1:
        return this.basisInfoForm;
      case State.Step2:
        return this.equipmentInventoryForm;
      case State.Step3:
        return this.servicesForm;
      case State.Step4:
        return this.pricingForm;
      case State.Step5:
        return this.contractForm;
    }
  }

  nextStep() {
    if (!this.projectId) return;
    const formCurrentState: FormGroup = this.getFormGroup(this.stateMaschine.currentState);
    this.dataService.tryWriteData(formCurrentState, this.projectId).subscribe(isWritingSuccessful => {
      if (isWritingSuccessful) {
        this.stateMaschine.forward(this.isSimpleCalculation);
      }
    });
  }

  stepBack() {
    if (!this.projectId) return;
    const formCurrentState: FormGroup = this.getFormGroup(this.stateMaschine.currentState);
    this.dataService.tryWriteData(formCurrentState, this.projectId).subscribe(isWritingSuccessful => {
      if (isWritingSuccessful) {
        this.stateMaschine.back(this.isSimpleCalculation);
      }
    });
  }

  backButtonClick() {
    this.router.navigate(['../../'], { relativeTo: this.activatedRoute });
  }
}

export enum State {
  Step1,
  Step2,
  Step3,
  Step4,
  Step5
}

interface WorkflowStep {
  from: State;
  to: State;
}

const advancedCalcTransitions: WorkflowStep[] = [
  { from: State.Step1, to: State.Step2 },
  { from: State.Step2, to: State.Step3 },
  { from: State.Step3, to: State.Step4 },
  { from: State.Step4, to: State.Step5 }
];

const simpleCalcTransitions: WorkflowStep[] = [
  { from: State.Step1, to: State.Step4 },
  { from: State.Step4, to: State.Step5 }
];

class WorkflowStateMachine {
  currentState: State = State.Step1;

  public forward(isSimpleCalculation: boolean): void {
    const currentStateIndex = this.currentState;
    const transitions = isSimpleCalculation ? simpleCalcTransitions : advancedCalcTransitions;
    const transition = transitions.find(t => t.from === currentStateIndex);

    if (transition) {
      this.currentState = transition.to;
    } else {
      throw new Error('Invalid transition');
    }
  }

  public back(isSimpleCalculation: boolean): void {
    const currentStateIndex = this.currentState;
    const transitions = isSimpleCalculation ? simpleCalcTransitions : advancedCalcTransitions;
    const transition = transitions.find(t => t.to === currentStateIndex);

    if (transition) {
      this.currentState = transition.from;
    } else {
      throw new Error('Invalid transition');
    }
  }

  public setState(state: State): void {
    this.currentState = state;
  }
}
